import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, SearchResultsDefault as SearchResultsComponent, type SearchResult, type SearchFilter } from '@airclou/ui';
import { scrollContentToTop } from '@airclou/utils';
import {
  Search,
  Users,
  Building2,
  LayoutGrid
} from 'lucide-react';
import { searchService, SearchFilters } from '../../services/searchService';
import { ErrorState } from '../../components/state/ErrorState';
import { useTimezone } from '@airclou/shell/timezone'

export default function DashboardSearch() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // The mount effect always searches, so the first paint is a search in flight.
  // At false, `showEmptyState` was true for that first frame and the page
  // flashed "No results found" before the spinner.
  const [isLoading, setIsLoading] = useState(true);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const pendingRequestIdRef = useRef<string | null>(null);

  const initialQuery = searchParams.get('q') || '';
  const initialFilter = searchParams.get('type') || 'all';
  // A hand-typed `?page=` can be anything. Both the search on mount and Retry
  // read this, and `page=NaN` / `page=0` reach the API as a bad request — so
  // does `page=99999999999999999999`, which parses to a finite 1e20 and lands
  // as an offset past bigint range, hence the safe-integer test rather than
  // `Number.isFinite`.
  const parsedPage = Number.parseInt(searchParams.get('page') || '1', 10);
  const initialPage = Number.isSafeInteger(parsedPage) && parsedPage > 0 ? parsedPage : 1;

  const dashboardSearchFilters: SearchFilter[] = [
    { value: 'all', label: t('search.allResults'), icon: Search },
    { value: 'member', label: t('search.teamMembers'), icon: Users },
    { value: 'company', label: t('search.companies'), icon: Building2 },
    { value: 'workspace', label: t('common.workspaces'), icon: LayoutGrid }
  ];

  const performSearch = useCallback(async (query: string, filter: string, page: number = 1) => {
    setIsLoading(true);
    setError(null);

    const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    pendingRequestIdRef.current = requestId;

    try {
      const filters: SearchFilters = {
        type: filter === 'all' ? 'all' : filter
      };

      const { response } = await searchService.search(query, filters, { page, limit: 20 });

      if (pendingRequestIdRef.current === requestId) {
        setSearchResults(response.results);
        setTotalResults(response.total);
        setCurrentPage(page);
      }
    } catch (err) {
      if (pendingRequestIdRef.current === requestId) {
        setError(t('search.failedToSearch'));
        setSearchResults([]);
        setTotalResults(0);
      }
    } finally {
      if (pendingRequestIdRef.current === requestId) {
        setIsLoading(false);
      }
    }
  }, [t]);

  // A search runs when this effect sees the URL's (query, filter, page) change,
  // so the handlers below write the URL and leave the fetching here. They used
  // to call `performSearch` themselves as well, which cost three searches per
  // filter click — the component reset the page before changing the filter, so
  // the first of the three even carried the old filter. Both halves of that are
  // gone. Two paths still fetch directly, each for a stated reason: `applyParams`
  // below, and Retry, which re-runs a URL that never changed.
  useEffect(() => {
    performSearch(initialQuery, initialFilter, initialPage);
  }, [performSearch, initialQuery, initialFilter, initialPage]);

  // The effect above is what fetches, and it fires on the tuple it reads —
  // query, filter, page — not on the URL string. So a click can rewrite the URL
  // and still not search: `?q=sales&page=1` → `?q=sales` is a different string
  // with an identical tuple (the page normalizes to 1 either way), as is
  // dropping an explicit `?type=all`. Writing the URL and deciding the fetch are
  // therefore two separate questions, and a click that answers no to the second
  // has to search here or it does nothing at all — which matters most after a
  // failure, where the filter row is the operator's way out.
  const applyParams = (params: URLSearchParams, page: number) => {
    const nextQuery = params.get('q') || '';
    const nextFilter = params.get('type') || 'all';
    const effectWillFetch =
      nextQuery !== initialQuery || nextFilter !== initialFilter || page !== initialPage;

    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params);
    }
    if (!effectWillFetch) {
      performSearch(nextQuery, nextFilter, page);
    }
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.url);
  };

  const handleSearch = (query: string) => {
    const params = new URLSearchParams(searchParams);
    if (query.trim()) {
      params.set('q', query);
    } else {
      params.delete('q');
    }
    params.delete('page');
    applyParams(params, 1);
  };

  const handleFilterChange = (filter: string) => {
    const params = new URLSearchParams(searchParams);
    if (filter !== 'all') {
      params.set('type', filter);
    } else {
      params.delete('type');
    }
    params.delete('page');
    applyParams(params, 1);
    // A new result set replaces the list, so the content area goes back to the
    // top. This used to arrive via the component's `onPageChange(1)`, which it
    // no longer calls for a consumer that owns filtering.
    scrollContentToTop();
  };

  const handleClearFilters = () => {
    const params = new URLSearchParams();
    params.set('q', '');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' });
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page > 1) {
      params.set('page', page.toString());
    } else {
      params.delete('page');
    }
    applyParams(params, page);
    scrollContentToTop();
  };

  const totalPages = Math.ceil(totalResults / 20);

  return (
    <div className="space-y-6">
      <PageHeader title={t('search.searchResults')} />

      <SearchResultsComponent
        timezone={timezone}
        results={searchResults}
        filters={dashboardSearchFilters}
        onResultClick={handleResultClick}
        onSearch={handleSearch}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        isLoading={isLoading}
        initialQuery={initialQuery}
        initialFilter={initialFilter}
        currentPage={currentPage}
        totalPages={totalPages}
        totalResults={totalResults}
        onPageChange={handlePageChange}
        // Replaces the results region only, so the filter row survives a failed
        // search. Retries the page the URL asks for — `currentPage` is only set
        // by a search that succeeded, so it would silently retry page 1.
        errorState={
          error ? (
            <ErrorState
              title={t('search.searchFailed')}
              description={error}
              onRetry={() => performSearch(initialQuery, initialFilter, initialPage)}
            />
          ) : undefined
        }
        config={{
          // No `title` — the page's own PageHeader is the heading. The copy
          // below is unconditional: the component shows the empty state only
          // when it has no results, isn't loading and has no `errorState`,
          // which is the whole of what the page's own condition used to say.
          emptyStateTitle: t('search.noResultsFound'),
          emptyStateDescription: t('search.adjustSearch'),
          clearSearchUrl: '/search',
          itemsPerPage: 20,
          showGroupedResults: false
        }}
      />
    </div>
  );
}
